import { globalEvent } from '@shared/easyfy.core';
import { DataMatch, CreditCard, CreditCardVerify, CreditCardRequest, IDCardVerify, GetRequestToProvider } from '../services/ageverification.service';
import { setCookie, UrlHistoryContent } from '@clientShared/utils';
import PageRender from '@clientShared/pagerender';

declare const window: any;

export const AgeVerificationCookie: string = 'AgeVerification';

export const AvProvider = {
  AgeChecked: 'AgeChecked',
  Yoti: 'Yoti',
};

const VerifyMethod = {
  AgeEstimation: 'AgeEstimation',
  DocScan: 'DocScan',
};

export default class AgeVerification {
  private _containerSelector: string;
  private _container: HTMLElement;
  private _orderNumber: string = '';
  private _checkoutId: string = '';
  private _page: string = '';

  private history: Map<string, UrlHistoryContent> = new Map();
  static Instance: AgeVerification;
  private pageRender: PageRender;
  constructor(containerSelector: string, orderNumber: string, checkoutId: string, page: string) {
    this._containerSelector = containerSelector;
    this._orderNumber = orderNumber;
    this._checkoutId = checkoutId;
    this._page = page;

    globalEvent({
      type: 'click',
      events: [
        {
          action: 'agechecked-idcard',
          callback: this.onAgeCheckedIdCard.bind(this),
        },
        {
          action: 'agechecked-creditcard',
          callback: this.onCreditcard.bind(this),
        },
        {
          action: 'agechecked-creditcard-verify',
          callback: this.onCreditcardVerify.bind(this),
        },
        {
          action: 'yoti-ageestimation',
          callback: this.onYotiAgeEstimation.bind(this),
        },
        ,
        {
          action: 'yoti-docscan',
          callback: this.onYotiDocScan.bind(this),
        },
      ],
    });

    this._container = document.querySelector(this._containerSelector);
    this.pageRender = new PageRender(this._container);

    window.addEventListener('popstate', (event) => {
      if (event.state) {
        const _hist = this.history.get(event.state.key);
        this.pageRender.ReplaceContent(_hist.data, _hist.appendType, true);

        if (event.state.key === 'init') {
          this.onDataMatch(this._orderNumber, this._checkoutId);
        }
      }
    });
  }

  onDataMatch = async (orderNumber: string, checkoutId: string) => {
    try {
      const response = await DataMatch(orderNumber, checkoutId);

      if (response.status !== 200) {
        this.pageRender.RenderAgeVerificationError();
        return;
      }

      if (response?.data?.partialHtml || response?.data?.orderSummary)
        this.setNewUrl('verify', response?.data?.partialHtml ?? response?.data?.orderSummary, '');

      this.pageRender.Render(response?.data);
    } catch (error) {
      this.pageRender.RenderAgeVerificationError();
    }
  };

  onGetRequestToProvider = async (orderNumber: string, verifyMethod: string) => {
    try {
      const response = await GetRequestToProvider(orderNumber, verifyMethod);

      this.sendRequestToProvider(orderNumber, verifyMethod, response);
    } catch (error) {
      this.pageRender.RenderAgeVerificationError();
    }
  };

  sendRequestToProvider = async (orderNumber: string, verifyMethod: string, response: any) => {
    if (response?.status === 200 && response?.data?.providerUrl) {
      this.setYotiCookie(response?.data?.providerTransactionId, verifyMethod, orderNumber);

      this.redirectToProvider(response?.data?.providerUrl);

      return;
    }

    if (response?.data?.partialHtml || response?.data?.orderSummary) this.setNewUrl('verify', response?.data?.partialHtml ?? response?.data?.orderSummary, '');

    this.pageRender.Render(response?.data ?? '');
  };

  onCreditcard = async () => {
    try {
      const response = await CreditCard();
      if (response?.status === 200 && response?.data?.partialHtml) {
        this.setNewUrl('creditcard', response?.data?.partialHtml, '');
        this.pageRender.Render(response?.data ?? '');
      }
    } catch (error) {
      this.pageRender.RenderAgeVerificationError();
    }
  };

  onCreditcardVerify = async () => {
    try {
      const cardNumber: HTMLInputElement = document.getElementById('cardNumber') as HTMLInputElement;
      const validDate: HTMLInputElement = document.getElementById('validDate') as HTMLInputElement;
      const token: HTMLInputElement = document.querySelector('[name="__RequestVerificationToken"]');
      const data: CreditCardRequest = {
        checkoutId: this._checkoutId || '',
        cardNumber: cardNumber.value,
        validDate: validDate.value,
        orderNumber: this._orderNumber,
      };

      const response = await CreditCardVerify(data, token.value || '');
      if (response?.status === 200) {
        if (response?.data?.errorMessage) {
          const errorMessage: HTMLElement = document.querySelector('[data-container="error"]') as HTMLElement;
          errorMessage.innerText = response?.data.errorMessage;
          return;
        }

        if (response?.data?.status == 'Approved') {
          this.pageRender.Render(response?.data ?? '');
          return;
        }

        this.setNewUrl('creditcardVerify', response?.data?.partialHtml ?? response?.data?.orderSummary, '');
        this.pageRender.Render(response?.data ?? '');
      }
    } catch (error) {
      this.pageRender.RenderAgeVerificationError();
    }
  };

  onAgeCheckedIdCard = async (e: Event) => {
    e.preventDefault();
    const el: HTMLAnchorElement = e.target as HTMLAnchorElement;

    if (el.dataset.state === 'processing') return false;
    el.dataset.state = 'processing';

    try {
      const response = await IDCardVerify(this._orderNumber);
      if (response?.status === 200 && response?.data?.providerUrl) {
        this.setAgeCheckedCookie(response?.data?.providerTransactionId, response?.data?.providerTransactionToken, this._orderNumber);

        this.redirectToProvider(response?.data?.providerUrl);

        return;
      }

      if (response?.data?.partialHtml || response?.data?.orderSummary)
        this.setNewUrl('verify', response?.data?.partialHtml ?? response?.data?.orderSummary, '');

      this.pageRender.Render(response?.data ?? '');

      delete el.dataset.state;
    } catch (error) {
      delete el.dataset.state;
      this.pageRender.RenderAgeVerificationError();
    }
  };

  onYotiDocScan = async (e: Event) => this.handleYotiRequest(e, VerifyMethod.DocScan);

  onYotiAgeEstimation = async (e: Event) => this.handleYotiRequest(e, VerifyMethod.AgeEstimation);

  handleYotiRequest = async (e: Event, method: string) => {
    e.preventDefault();
    const el = e.target as HTMLAnchorElement;

    if (el.dataset.state === 'processing') return;
    el.dataset.state = 'processing';

    await this.onGetRequestToProvider(this._orderNumber, method);

    delete el.dataset.state;
  };

  setYotiCookie = (sessionId: string, verifyMethod: string, orderNumber: string) => {
    var contentCookie = `{"sessionId": "${sessionId}", "verifyMethod":"${verifyMethod}", "page":"${this._page}", "checkoutId":"${this._checkoutId}", "orderNumber":"${orderNumber}"}`;

    setCookie(contentCookie, AgeVerificationCookie);
  };

  setAgeCheckedCookie = (ageCheckId: string, ageVerifiedId: string, orderNumber: string) => {
    var contentCookie = `{"agecheckId": "${ageCheckId}", "ageVerifiedId":"${ageVerifiedId}", "page":"${this._page}", "checkoutId":"${this._checkoutId}", "orderNumber":"${orderNumber}"}`;

    setCookie(contentCookie, AgeVerificationCookie);
  };

  setNewUrl(valueKey: string, data: string, appendType: string) {
    if (history) {
      this.history.set(valueKey, {
        data: data,
        appendType: appendType,
      });
    }

    history.pushState({ key: valueKey }, null, window.location.href);
  }

  redirectToProvider = async (providerUrl: string) => {
    window.location.href = providerUrl;
  };
}
